import {
  CRON_JOBS_BULK_DELETE_URL,
  CRON_JOBS_BULK_TOGGLE_URL,
  CRON_JOBS_URL,
  cronJobRunDetailUrl,
  cronJobRunsUrl,
  cronJobToggleUrl,
  cronJobTriggerUrl,
  cronJobUrl,
  SCHEDULE_INTERPRET_URL,
} from "../constants/automation-api-urls";
import type {
  IAgentRunDetail,
  IBulkDeleteCronJobPayload,
  IBulkOperationResult,
  IBulkToggleCronJobPayload,
  ICreateCronJobPayload,
  ICronJob,
  ICronRun,
  ICronRunsListResponse,
  IInterpretSchedulePayload,
  IScheduleInterpretationResult,
} from "../types";
import type {
  IGetCronJobRunsProps,
  IGetRunDetailProps,
  IUpdateCronJobProps,
} from "./automation.types";
import type { ApiClient } from "./client";

export class CronJobApi {
  constructor(private apiClient: ApiClient) {}

  async getCronJobs(): Promise<ICronJob[]> {
    const response = await this.apiClient.get<ICronJob[]>(CRON_JOBS_URL);
    return response.data;
  }

  async getCronJob(uuid: string): Promise<ICronJob> {
    const response = await this.apiClient.get<ICronJob>(cronJobUrl(uuid));
    return response.data;
  }

  async createCronJob(data: ICreateCronJobPayload): Promise<ICronJob> {
    const response = await this.apiClient.post<ICronJob>(CRON_JOBS_URL, data);
    return response.data;
  }

  // NOTE: Persists nothing — the caller saves the result via create/update.
  async interpretSchedule(
    data: IInterpretSchedulePayload,
  ): Promise<IScheduleInterpretationResult> {
    const response = await this.apiClient.post<IScheduleInterpretationResult>(
      SCHEDULE_INTERPRET_URL,
      data,
    );
    return response.data;
  }

  async updateCronJob({ uuid, data }: IUpdateCronJobProps): Promise<ICronJob> {
    const response = await this.apiClient.put<ICronJob>(cronJobUrl(uuid), data);
    return response.data;
  }

  async deleteCronJob(uuid: string): Promise<void> {
    await this.apiClient.delete(cronJobUrl(uuid));
  }

  async toggleCronJob(uuid: string): Promise<ICronJob> {
    const response = await this.apiClient.put<ICronJob>(cronJobToggleUrl(uuid));
    return response.data;
  }

  async bulkToggleCronJobs(
    payload: IBulkToggleCronJobPayload,
  ): Promise<IBulkOperationResult> {
    const response = await this.apiClient.put<IBulkOperationResult>(
      CRON_JOBS_BULK_TOGGLE_URL,
      payload,
    );
    return response.data;
  }

  async bulkDeleteCronJobs(
    payload: IBulkDeleteCronJobPayload,
  ): Promise<IBulkOperationResult> {
    const response = await this.apiClient.delete<IBulkOperationResult>(
      CRON_JOBS_BULK_DELETE_URL,
      { data: payload },
    );
    return response.data;
  }

  async triggerCronJob(uuid: string): Promise<void> {
    await this.apiClient.post(cronJobTriggerUrl(uuid));
  }

  async getCronJobRuns({
    uuid,
    page,
    limit,
  }: IGetCronJobRunsProps): Promise<ICronRunsListResponse> {
    const response = await this.apiClient.get<ICronRunsListResponse>(
      cronJobRunsUrl({ uuid, page, limit }),
    );
    return response.data;
  }

  // NOTE: NSAI-5266 — per-run detail (the run + its ordered step trace).
  async getCronJobRunDetail({
    uuid,
    runUuid,
  }: IGetRunDetailProps): Promise<IAgentRunDetail<ICronRun>> {
    const response = await this.apiClient.get<IAgentRunDetail<ICronRun>>(
      cronJobRunDetailUrl({ uuid, runUuid }),
    );
    return response.data;
  }
}
